import React, { useEffect, useState } from 'react'
import type {
  MarketplaceJobCreateRequest,
  MarketplaceStlOption
} from '../../../services/backendService'
import type { MarketplaceJobDraft } from '../../../types/windows'
import '../styles/JobCreationForm.css'

interface JobCreationFormProps {
  stlOptions: MarketplaceStlOption[]
  initialValues?: MarketplaceJobDraft
  isLoadingStlOptions?: boolean
  isSubmitting?: boolean
  errorMessage?: string | null
  onSubmit: (jobData: MarketplaceJobCreateRequest) => Promise<void> | void
  onCancel: () => void
}

const getTodayDateString = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const buildInitialFormData = (initialValues?: MarketplaceJobDraft) => ({
  stlFileId: initialValues?.stlFileId || '',
  title: initialValues?.title || '',
  description: initialValues?.description || '',
  material: initialValues?.material || 'PLA',
  customMaterial: '',
  qualityLevel: initialValues?.qualityLevel || 'standard',
  deadline: initialValues?.deadline || '',
  pricingModel: (initialValues?.pricingModel || 'bid') as 'bid' | 'fixed_price',
  fixedPrice: initialValues?.fixedPrice || ''
})

export const JobCreationForm: React.FC<JobCreationFormProps> = ({
  stlOptions,
  initialValues,
  isLoadingStlOptions = false,
  isSubmitting = false,
  errorMessage,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState(buildInitialFormData(initialValues))
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    setFormData(buildInitialFormData(initialValues))
    setLocalError(null)
  }, [initialValues])

  useEffect(() => {
    const resolvedStlFileId = initialValues?.stlFileId || (
      initialValues?.projectId
        ? stlOptions.find(option => option.projectId === initialValues.projectId)?.stlFileId
        : undefined
    )

    if (!resolvedStlFileId) {
      return
    }

    setFormData(current => (
      current.stlFileId === resolvedStlFileId
        ? current
        : { ...current, stlFileId: resolvedStlFileId }
    ))
  }, [initialValues?.projectId, initialValues?.stlFileId, stlOptions])

  const resolvedMaterial = formData.material === 'other'
    ? formData.customMaterial.trim()
    : formData.material

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      fixedPrice: name === 'pricingModel' && value !== 'fixed_price' ? '' : prev.fixedPrice,
      [name]: value
    }))
    setLocalError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
        const selectedMaterial = resolvedMaterial

        if (!selectedMaterial) {
          setLocalError('Specify the material before publishing this job.')
          return
        }

    e.preventDefault()

    const title = formData.title.trim()
    const description = formData.description.trim()
    const today = getTodayDateString()

    if (!title) {
      setLocalError('Enter a job title before publishing to the marketplace.')
      return
    }

    if (!description) {
      setLocalError('Add a short description so farms understand the print requirements.')
      return
    }

    if (formData.deadline < today) {
      setLocalError('Choose a deadline that is today or later.')
      return
    }

    let fixedPrice: number | undefined

    if (formData.pricingModel === 'fixed_price') {
      const parsedFixedPrice = parseFloat(formData.fixedPrice)

      if (!Number.isFinite(parsedFixedPrice) || parsedFixedPrice <= 0) {
        setLocalError('Enter a fixed price greater than 0 to request direct quotes.')
        return
      }

      fixedPrice = parsedFixedPrice
    }

    setLocalError(null)

    await onSubmit({
      ...formData,
      title,
      description,
      material: selectedMaterial,
      fixedPrice
    })
  }

  const submitDisabled = isSubmitting || isLoadingStlOptions || stlOptions.length === 0
  const displayedError = localError || errorMessage

  return (
    <form className="job-creation-form" onSubmit={handleSubmit}>
      <h3>Create New Print Job</h3>

      <div className="form-group">
        <label>STL File *</label>
        {isLoadingStlOptions ? (
          <div className="form-help">Loading STL-backed projects...</div>
        ) : stlOptions.length === 0 ? (
          <div className="form-empty-state">
            No STL-backed projects are available yet. Upload an STL_IN file to a project before creating a marketplace job.
          </div>
        ) : (
          <>
            <select
              name="stlFileId"
              value={formData.stlFileId}
              onChange={handleChange}
              required
            >
              <option value="">Select an STL-backed project</option>
              {stlOptions.map(option => (
                <option key={option.stlFileId} value={option.stlFileId}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="form-help">Choose the project/STL that this marketplace job should publish.</div>
          </>
        )}
      </div>

      <div className="form-group">
        <label>Job Title *</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="e.g., Custom Insole - Left Foot"
          required
        />
        {initialValues?.projectName && (
          <div className="form-help">
            Launch context: {initialValues.projectName}
            {initialValues.patientName ? ` • ${initialValues.patientName}` : ''}
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Description *</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Describe your print job requirements..."
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Material *</label>
          <select name="material" value={formData.material} onChange={handleChange}>
            <option>PLA</option>
            <option>PETG</option>
            <option>TPU</option>
            <option>Nylon</option>
            <option value="other">Other (specify)</option>
          </select>
          {formData.material === 'PLA' && (
            <div className="form-help">Polylactic acid — rigid, biodegradable, easy to print. Good for standard, low-flex insoles.</div>
          )}
          {formData.material === 'PETG' && (
            <div className="form-help">Polyethylene terephthalate glycol — semi-flexible, moisture-resistant. Good balance of stiffness and durability.</div>
          )}
          {formData.material === 'TPU' && (
            <div className="form-help">Thermoplastic polyurethane — flexible, rubber-like. Ideal for cushioning and shock-absorbing insoles.</div>
          )}
          {formData.material === 'Nylon' && (
            <div className="form-help">Nylon (PA) — strong, wear-resistant, slightly flexible. Suitable for high-stress or sport insoles.</div>
          )}
          {formData.material === 'other' && (
            <input
              type="text"
              name="customMaterial"
              value={formData.customMaterial}
              onChange={handleChange}
              placeholder="e.g., Carbon Fiber Nylon"
              style={{ marginTop: '8px' }}
            />
          )}
        </div>

        <div className="form-group">
          <label>Quality Level *</label>
          <select name="qualityLevel" value={formData.qualityLevel} onChange={handleChange}>
            <option value="draft">Draft</option>
            <option value="standard">Standard</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Deadline *</label>
        <input
          type="date"
          name="deadline"
          value={formData.deadline}
          onChange={handleChange}
          required
        />
        <div className="form-help">Choose today or a future date for farm delivery commitments.</div>
      </div>

      <div className="form-group">
        <label>Pricing Model *</label>
        <select name="pricingModel" value={formData.pricingModel} onChange={handleChange}>
          <option value="bid">Bid System (Farms bid on your job)</option>
          <option value="fixed_price">Fixed Price (You set the price)</option>
        </select>
        <div className="form-help">Use bids for competitive offers or fixed price when you want farms to quote against a target amount.</div>
      </div>

      {formData.pricingModel === 'fixed_price' && (
        <div className="form-group">
          <label>Fixed Price ($) *</label>
          <input
            type="number"
            name="fixedPrice"
            value={formData.fixedPrice}
            onChange={handleChange}
            placeholder="0.00"
            step="0.01"
            min="0"
            required
          />
        </div>
      )}

      {displayedError && <div className="form-error">{displayedError}</div>}

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={submitDisabled}>
          {isSubmitting ? 'Creating Job...' : 'Create Job'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </button>
      </div>
    </form>
  )
}

